import React, { FC, ReactElement, ReactNode, useState } from 'react';
import {
  AddUniversal,
  MoreHorizontalUniversal,
  TrashCanOutlined,
} from '@duxton-web/icons';
import {
  Button,
  ConfigProvider,
  Dropdown,
  MenuProps,
  Tag,
  Tooltip,
} from '@duxton-web/ui';

import { SecondaryText } from '../components';
import ConstraintEditor from './Constraint';
import { ConstraintOperator } from './Constraint/facetMap';
import { Constraint, ConstraintType, VariableInfo } from '@/lib/PlannerDTOs';

export interface CosntraintsEditorFacet {
  label?: string;
  value?: string;
}

interface ConstraintsEditorProps {
  constraints: Constraint[];
  disabled?: boolean;
  allowedFacets: CosntraintsEditorFacet[];
  onlyEqualOperator?: boolean;
  onChange: (constraints: Constraint[]) => void;
  skipFirst?: boolean; // set to true if you want to render the AND tag for the first constraint
  disableAddConstraint?: boolean;
  disableAddCustomConstraint?: boolean;
  disableDeleteConstraint?: boolean;
  disableDeleteCustomConstraint?: boolean;
  rolloutVariable?: VariableInfo;
  disableTarget?: boolean;
  showFacetStatus?: boolean;
  testingPurpose?: boolean;
  mandatoryFacets?: string[]; // Facets that cannot be deleted from constraints
}

export const DEFAULT_CONSTRAINT: Constraint = {
  target: '',
  op: '',
  type: ConstraintType.Standard,
  value: '',
};

function AndTag(props): ReactElement {
  const { index = -999, skipFirst } = props;

  if (skipFirst && index === 0) return <></>;

  return (
    <Tag
      size="small"
      style={{
        paddingBlock: 8,
        marginInlineEnd: 0,
      }}
    >
      <SecondaryText strong>AND</SecondaryText>
    </Tag>
  );
}

function DeleteButton(props): ReactElement {
  const {
    index,
    target,
    allowedFacets,
    constraints,
    highlightIndex,
    disabled,
    disableDeleteConstraint,
    disableDeleteCustomConstraint,
    mandatoryFacets,
    onChange,
    onHoverDelete,
    onMouseOutDelete,
  } = props;

  function onConstraintDelete(index: number): void {
    const constraintsCopy = [...constraints];

    constraintsCopy.splice(index, 1);

    onChange(constraintsCopy);
  }

  if (disabled) return <></>;

  const isMainFacet = allowedFacets.find(
    (allowedFacet) => allowedFacet.value === target,
  );

  // Check if this constraint is mandatory and the first occurrence
  const isMandatory = mandatoryFacets?.includes(target);
  const firstOccurrenceIndex = constraints.findIndex(
    (c) => c.target === target,
  );
  const isFirstMandatoryOccurrence =
    isMandatory && index === firstOccurrenceIndex;

  if (disableDeleteConstraint && isMainFacet) return <></>;

  if (disableDeleteCustomConstraint && !isMainFacet) return <></>;

  const deleteButton = (
    <ConfigProvider
      theme={{
        token: {
          fontWeightStrong: 400,
        },
        components: {
          Button: {
            onlyIconSize: 14,
            defaultBorderColor: '#bfbfbf',
          },
        },
      }}
    >
      <Button
        data-testid="constraint-delete"
        danger={index === highlightIndex}
        shape="default"
        style={{
          flex: '0 0 40px',
          alignSelf: 'flex-start',
        }}
        icon={<TrashCanOutlined />}
        onClick={() => onConstraintDelete(index)}
        onMouseOver={() => {
          // Don't highlight when disabled (mandatory constraint)
          if (!isFirstMandatoryOccurrence) {
            onHoverDelete(index);
          }
        }}
        onMouseOut={() => {
          if (!isFirstMandatoryOccurrence) {
            onMouseOutDelete(-1);
          }
        }}
        disabled={isFirstMandatoryOccurrence}
      />
    </ConfigProvider>
  );

  // Need to wrap in a span to make tooltip work with disabled button
  if (isFirstMandatoryOccurrence) {
    return (
      <Tooltip title="This constraint is mandatory and cannot be removed">
        <span style={{ display: 'inline-block' }}>{deleteButton}</span>
      </Tooltip>
    );
  }

  return deleteButton;
}

function AddFacetConstraint(props): ReactElement {
  const { onlyEqualOperator, disableAddConstraint, onAddConstraint } = props;

  if (disableAddConstraint) return <></>;

  return (
    <Button
      shape="default"
      icon={<AddUniversal />}
      onClick={() => {
        const constraint: Constraint = { ...DEFAULT_CONSTRAINT };

        if (onlyEqualOperator) constraint.op = ConstraintOperator.EQUALS.value;

        onAddConstraint(constraint);
      }}
    >
      Add Constraint
    </Button>
  );
}

function AddCustomConstraint(props): ReactElement {
  const {
    onlyEqualOperator,
    disableAddConstraint,
    disableAddCustomConstraint,
    onAddConstraint,
  } = props;

  if (disableAddCustomConstraint) return <></>;

  const onClick: MenuProps['onClick'] = ({ key }) => {
    let constraint;

    switch (key) {
      case ConstraintType.CustomString:
        constraint = {
          ...DEFAULT_CONSTRAINT,
          type: ConstraintType.CustomString,
        };
        break;
      case ConstraintType.CustomInt:
        constraint = { ...DEFAULT_CONSTRAINT, type: ConstraintType.CustomInt };
        break;
      default:
        break;
    }

    if (onlyEqualOperator) constraint.op = ConstraintOperator.EQUALS.value;

    onAddConstraint(constraint);
  };

  const items: MenuProps['items'] = [
    {
      key: ConstraintType.CustomString,
      label: 'Add Custom Key with String Value',
      icon: <AddUniversal />,
    },
    {
      key: ConstraintType.CustomInt,
      label: 'Add Custom Key with Integer Value',
      icon: <AddUniversal />,
    },
  ];

  if (!disableAddConstraint)
    return (
      <Dropdown menu={{ items, onClick }}>
        <Button shape="default" icon={<MoreHorizontalUniversal />} />
      </Dropdown>
    );

  return (
    <Dropdown menu={{ items, onClick }}>
      <Button shape="default" icon={<AddUniversal />}>
        Add Custom Constraint
      </Button>
    </Dropdown>
  );
}

function AddConstraintWrapper(props): ReactElement {
  const {
    onlyEqualOperator,
    disabled,
    disableAddConstraint,
    disableAddCustomConstraint,
    constraints,
    skipFirst,
    onChange,
  } = props;

  function onAddConstraint(constraint: Constraint): void {
    const constraintsCopy = [...constraints];

    onChange([...constraintsCopy, constraint]);
  }

  if (disabled || (disableAddConstraint && disableAddCustomConstraint))
    return <></>;

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <AndTag index={constraints?.length} skipFirst={skipFirst} />
      <ConfigProvider
        theme={{
          token: {
            fontWeightStrong: 400,
          },
          components: {
            Button: {
              contentFontSize: 14,
              defaultBorderColor: '#bfbfbf',
            },
          },
        }}
      >
        <AddFacetConstraint
          onlyEqualOperator={onlyEqualOperator}
          onAddConstraint={onAddConstraint}
          disableAddConstraint={disableAddConstraint}
        />

        <AddCustomConstraint
          onlyEqualOperator={onlyEqualOperator}
          disableAddConstraint={disableAddConstraint}
          disableAddCustomConstraint={disableAddCustomConstraint}
          onAddConstraint={onAddConstraint}
        />
      </ConfigProvider>
    </div>
  );
}

const ConstraintsEditor: FC<ConstraintsEditorProps> = ({
  constraints = [],
  disabled,
  allowedFacets,
  onlyEqualOperator = false,
  onChange,
  skipFirst,
  disableAddConstraint,
  disableAddCustomConstraint,
  disableDeleteConstraint,
  disableDeleteCustomConstraint,
  rolloutVariable,
  disableTarget,
  showFacetStatus = true,
  testingPurpose,
  mandatoryFacets = [],
}) => {
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);

  function showConstraint(constraint: Constraint): boolean {
    switch (constraint.op) {
      case ConstraintOperator.SLOT.value:
        return false;
      default:
        return true;
    }
  }

  function onConstraintUpdate(index: number, constraint: Constraint): void {
    const constraintsCopy = [...constraints];

    constraintsCopy[index] = constraint;

    onChange(constraintsCopy);
  }

  const constraintRows: ReactNode[] = [];

  constraints.forEach((constraint: Constraint, index: number) => {
    if (!showConstraint(constraint)) return false;

    constraintRows.push(
      <div
        key={`constraint-${index}`}
        data-testid={`constraint-${index}`}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <AndTag index={index} skipFirst={skipFirst} />
        <ConstraintEditor
          hasError={highlightIndex === index}
          disabled={disabled}
          index={index}
          constraint={constraint}
          isCustomConstraint={constraint.type !== ConstraintType.Standard}
          allowedFacets={allowedFacets}
          onlyEqualOperator={onlyEqualOperator}
          onChange={onConstraintUpdate}
          rolloutVariable={rolloutVariable}
          showFacetStatus={showFacetStatus}
          disableTarget={disableTarget}
          testingPurpose={testingPurpose}
          mandatoryFacets={mandatoryFacets}
          constraints={constraints}
        />
        <DeleteButton
          index={index}
          target={constraint?.target}
          allowedFacets={allowedFacets}
          constraints={constraints}
          highlightIndex={highlightIndex}
          disabled={disabled}
          disableDeleteConstraint={disableDeleteConstraint}
          disableDeleteCustomConstraint={disableDeleteCustomConstraint}
          mandatoryFacets={mandatoryFacets}
          onChange={onChange}
          onHoverDelete={setHighlightIndex}
          onMouseOutDelete={setHighlightIndex}
        />
      </div>,
    );
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {constraintRows}
      <AddConstraintWrapper
        onlyEqualOperator={onlyEqualOperator}
        disabled={disabled}
        disableAddConstraint={disableAddConstraint}
        disableAddCustomConstraint={disableAddCustomConstraint}
        constraints={constraints}
        skipFirst={skipFirst}
        onChange={onChange}
      />
    </div>
  );
};

export default ConstraintsEditor;
